import Editor from "../lib";
import {
  click,
  dragLeave,
  dragOver,
  drop,
  mouseLeave,
  mouseOver,
} from "../lib/eventHandlers";
import { HE } from "../types";

/**
 * Clones the HTML element, removes specific classes and attributes, and returns the modified HTML as a string.
 *
 * @param {HE} elem - the HTML element to be cloned
 * @return {HE} - the modified HTML as a string
 */
function getHtml(elem: HE) {
  const cloned = elem.cloneNode(true) as HE;
  const classesToRemove = "selected|mouseover";
  const attributesToRemove = "style|draggable|droppable|cmp-type";
  const classRegex = new RegExp(
    `(\\s*class\\s*=\\s*["'].*?\\s\\b)(${classesToRemove})(\\b\\s.*?["'])`,
    "gi"
  );
  const attributeRegex = new RegExp(
    `\\s(${attributesToRemove})\\s*=["'][^"']*?["']`,
    "gi"
  );

  cloned.classList.remove("selected", "mouseover");

  return cloned.outerHTML
    .replace(classRegex, "$1$3")
    .replace(/class=""/g, "")
    .replace(attributeRegex, "");
}

/**
 * Returns the CSS style of the given HTML element with line breaks after each style property.
 *
 * @param {HE} elem - the HTML element to retrieve the CSS style from
 * @return {string} the CSS style of the HTML element with line breaks after each style property
 */
function getCss(elem: HE) {
  return elem.getAttribute("style")?.replace(/;/g, ";\n");
}

/**
 * Generate HTML and CSS content for the given element and display them in the editor.
 *
 * @param {HE} elem - the element to generate HTML and CSS for
 * @param {Editor} editor - the editor to display the generated HTML and CSS
 */
function showHtmlCss(elem: HE, editor: Editor) {
  const css = getCss(elem);
  const html = getHtml(elem);

  if (html) {
    editor.htmlContainer.value = html;
  }

  if (css) {
    editor.cssContainer.value = css;
  }
}

/**
 * Parses the given HTML string and returns the children of the <body> element as an HTMLCollection.
 *
 * @param {string} htmlString - The HTML string to be parsed
 * @return {HTMLCollection | undefined} The children of the <body> element, or undefined if not found
 */
function parseHtmlString(htmlString: string): HTMLCollection | undefined {
  const parser = new DOMParser();
  const html = parser.parseFromString(htmlString, "text/html");

  return html.querySelector("body")?.children;
}

/**
 * Prepares an element by setting border style, adding event listeners for click, mouseover, mouseleave, dragover, dragleave, and drop.
 *
 * @param {HE} elem - the element to prepare
 * @param {Editor} editor - the editor to associate with the element
 * @return {HE} the prepared element
 */
function prepareElement(elem: HE, editor: Editor) {
  elem.addEventListener("click", (e) => click(e, editor));
  elem.addEventListener("mouseover", (e) => mouseOver(e, editor));
  elem.addEventListener("mouseleave", mouseLeave);
  elem.addEventListener("dragover", (e) => dragOver(e, elem));
  elem.addEventListener("dragleave", (e) => dragLeave(e, elem));
  elem.addEventListener("drop", (e) => drop(e, elem, editor));

  return elem;
}

export { getHtml, getCss, showHtmlCss, parseHtmlString, prepareElement };
